import { Link } from 'react-router-dom';
//import RecentSuccess from '../components/RecentSuccess';
import Seo from '../components/Seo';

export default function Home() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Seo
        title="Automatic Driving Lessons in Bexley & Greenwich | DVSA Approved Instructor"
        description="Learn automatic driving with a DVSA Approved Instructor. Area covered in Bexley and Greenwich boroughs."
        path="/"
      />
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-4 py-20 sm:py-32">
        <div className="text-center">
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
            Automatic Driving Lessons
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Bezy Driving School offers calm, structured lessons with a DVSA Approved Instructor. We cover key local areas in Bexley and Greenwich boroughs and provide flexible pricing to suit different learners.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/447449975074?text=Hello%2C%20I%20am%20interested%20in%20booking%20driving%20lessons%20with%20Bezy%20Driving%20School.%20Could%20you%20please%20provide%20information%20about%20availability%20and%20next%20steps%3F"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition inline-block text-center"
            >
              Book a Lesson
            </a>
            <Link
              to="/prices"
              className="bg-white hover:bg-gray-100 text-blue-600 font-bold py-3 px-8 rounded-lg border-2 border-blue-600 transition inline-block text-center"
            >
              View Prices
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">
            Why Learners Choose Bezy Driving School
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "👨‍🏫 DVSA Approved Instructor",
                description: "Fully qualified, registered and experienced instructor focused on safe, confidence-building progress."
              },
              {
                title: "⭐ Calm & Patient Lessons",
                description: "Structured lessons designed to build your confidence. No judgment, just professional guidance."
              },
              {
                title: "🎯 Tailored to Your Needs",
                description: "Whether you're nervous or confident, lessons are customized to your learning style."
              }
            ].map((feature, index) => (
              <div key={index} className="p-6 border border-gray-200 rounded-lg hover:shadow-lg transition">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas Section */}
      <section className="bg-gradient-to-br from-blue-50 to-green-50 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-4">
            Area Covered
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            We primarily cover Erith, Belvedere, Plumstead, Woolwich, Thamesmead, Abbey Wood, Welling, Bexley, Bexleyheath, Sidcup and Blackfen area with access to multiple test centres.
          </p>

          {/* Main Card - Modern Design */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Postcodes Section */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  🗺️ Postcodes
                </h3>
                <div className="flex flex-wrap gap-3">
                  {["DA8", "DA7", "DA6", "DA16", "DA17", "DA18", "SE18", "SE28", "SE2"].map((postcode, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-300 shadow-sm hover:shadow-md hover:scale-105 transition-all duration-200"
                    >
                      {postcode}
                    </span>
                  ))}
                </div>

                {/* Info Banner */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded-xl p-6 shadow-md mt-6">
                  <p className="text-gray-800 font-semibold">
                    <strong>Outside our main areas?</strong> Don't worry! Get in touch — we may be able to arrange lessons on a case-by-case basis depending on your location.
                  </p>
                </div>
              </div>

              {/* Test Centres Section */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  📝 Test Centres
                </h3>
                <div className="space-y-3">
                  {[
                    { name: "Erith", color: "from-blue-100 to-blue-200", textColor: "text-blue-800", borderColor: "border-blue-300" },
                    { name: "Belvedere", color: "from-blue-100 to-blue-200", textColor: "text-blue-800", borderColor: "border-blue-300" },
                    { name: "Sidcup", color: "from-blue-100 to-blue-200", textColor: "text-blue-800", borderColor: "border-blue-300" },
                    { name: "Bromley", color: "from-blue-100 to-blue-200", textColor: "text-blue-800", borderColor: "border-blue-300" }
                  ].map((centre, index) => (
                    <div
                      key={index}
                      className={`inline-flex items-center px-5 py-3 rounded-xl font-semibold bg-gradient-to-r ${centre.color} ${centre.textColor} border ${centre.borderColor} shadow-sm hover:shadow-md hover:scale-105 transition-all duration-200 w-full`}
                    >
                      <span className="mr-3 text-lg">📍</span>
                      {centre.name}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/*
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <RecentSuccess />
        </div>
      </section>
      */}

      {/* CTA Section */}
      <section className="bg-blue-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">🔥 Special Offer for New Students</h2>
          <p className="text-xl mb-8">
            Book your first lesson today and get clear, local guidance from a DVSA Approved Instructor at flexible rates.
          </p>
          <a
            href="https://wa.me/447449975074?text=Hello%2C%20I%20am%20interested%20in%20booking%20driving%20lessons%20with%20Bezy%20Driving%20School.%20Could%20you%20please%20provide%20information%20about%20availability%20and%20next%20steps%3F"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white hover:bg-gray-100 text-blue-600 font-bold py-3 px-8 rounded-lg transition inline-block"
          >
            Message Now to Book
          </a>
        </div>
      </section>
    </div>
  );
}
